<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { formatSi } from '@shell/utils/units';
import ScopeFilterDropdown from '@shell/components/lattice/ScopeFilterDropdown.vue';
import { nodePools, nodePoolLabels, NodeSnapshot } from '@shell/utils/lattice-node-overview';
export default defineComponent({
  components: { ScopeFilterDropdown },
  props:      {
    nodes: { type: Array as PropType<NodeSnapshot[]>, default: () => [] }, clusterId: { type: String, default: '' }, clusterName: { type: String, default: '' }
  },
  data() {
    return { selectedLabel: '' };
  },
  computed: {
    keys(): string[] {
      return nodePoolLabels(this.nodes);
    },
    activeLabel(): string {
      return this.keys.includes(this.selectedLabel) ? this.selectedLabel : this.keys[0] || '';
    },
    labelOptions(): any[] {
      return this.keys.map((key) => ({ value: key, label: key }));
    },
    pools() {
      return nodePools(this.nodes, this.activeLabel);
    },
  },
  methods: {
    capacity(overview: any): string {
      const cpu = overview.cpuReported ? `${ Number(overview.cpu.toFixed(3)) } cores` : 'CPU unavailable'; const memory = overview.memoryReported ? formatSi(overview.memory, {
        increment: 1024, suffix: 'iB', firstSuffix: 'B', maxPrecision: 1
      }) : 'memory unavailable';

      return `${ cpu } · ${ memory }`;
    },
    readiness(node: NodeSnapshot): string {
      const status = node.status?.conditions?.find((condition) => condition.type === 'Ready')?.status;

      return status === 'True' ? 'Ready' : status === 'False' ? 'Not ready' : 'Unknown';
    },
    location(node: NodeSnapshot): any {
      return {
        name:   'c-cluster-product-resource-id',
        params: {
          cluster: this.clusterId, product: 'explorer', resource: 'node', id: node.metadata?.name
        }
      };
    },
  },
});
</script>
<template>
  <div class="pool-membership">
    <div class="membership-toolbar">
      <div><strong>Node pools</strong><span>Membership from node labels · connectors do not represent network traffic</span></div><ScopeFilterDropdown
        v-if="keys.length"
        :value="activeLabel"
        :options="labelOptions"
        label="Pool label"
        @update:value="selectedLabel = $event"
      />
    </div>
    <div class="cluster-hub">
      <i
        class="icon icon-kubernetes"
        aria-hidden="true"
      /><strong>{{ clusterName || clusterId || 'Current cluster' }}</strong><span>{{ nodes.length }} loaded nodes · {{ pools.length }} {{ pools.length === 1 ? 'pool group' : 'pool groups' }}</span>
    </div>
    <p
      v-if="!activeLabel && nodes.length"
      class="pool-note"
    >
      No pool label found in the loaded nodes. Unassigned shows membership that has not been labelled.
    </p>
    <div
      v-if="pools.length"
      class="pool-grid"
    >
      <section
        v-for="pool in pools"
        :key="pool.key"
        class="pool-card"
        :aria-label="`Node pool ${pool.label}`"
      >
        <div class="pool-heading">
          <i
            class="icon icon-folder"
            aria-hidden="true"
          /><strong>{{ pool.label }}</strong><span>{{ pool.nodes.length }} nodes</span>
        </div><div class="pool-summary">
          <span v-if="pool.missing">No pool label</span>
          <span>{{ pool.overview.ready }} ready</span><span v-if="pool.overview.notReady">{{ pool.overview.notReady }} not ready</span><span v-if="pool.overview.unknown">{{ pool.overview.unknown }} unknown</span><span v-if="pool.overview.cordoned">{{ pool.overview.cordoned }} cordoned</span>
        </div><p class="pool-capacity">
          Reported allocatable: {{ capacity(pool.overview) }}<span>{{ pool.overview.cpuReported }}/{{ pool.nodes.length }} CPU reports · {{ pool.overview.memoryReported }}/{{ pool.nodes.length }} memory reports</span>
        </p><ul class="pool-nodes">
          <li
            v-for="(node,index) in pool.nodes"
            :key="node.metadata?.name || index"
          >
            <span
              class="readiness-dot"
              :class="{ ready: readiness(node) === 'Ready', unavailable: readiness(node) === 'Unknown' }"
              :aria-label="readiness(node)"
            /><RouterLink
              v-if="clusterId && node.metadata?.name"
              :to="location(node)"
            >
              {{ node.metadata.name }}
            </RouterLink><span v-else>{{ node.metadata?.name || 'Node name unavailable' }}</span><span class="node-state">{{ node.spec?.unschedulable ? 'Cordoned' : readiness(node) }}</span>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.pool-membership { padding: 14px 16px 16px; border-top: 1px solid var(--dev-border); }.membership-toolbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; > div:first-child { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px 14px; strong { font-size: 13px; font-weight: 500; } span { font-size: 12px; color: var(--dev-muted); } } :deep(.scope-filter) { width: 270px; } }
.cluster-hub { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; padding: 12px; border: 1px solid var(--dev-border); border-radius: 6px; margin: 14px 0 0; background: var(--dev-layer, var(--dev-surface)); strong { font-size: 14px; font-weight: 500; overflow-wrap: anywhere; } span { font-size: 12px; color: var(--dev-muted); margin-left: auto; }.icon { color: var(--dev-accent); } }
.pool-note { font-size: 12px; color: var(--dev-muted); margin: 10px 0 0; line-height: 1.5; }
.pool-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(min(260px,100%),1fr)); gap: 24px 12px; padding-top: 24px; max-height: 340px; overflow-y: auto; }.pool-card { min-width: 0; border: 1px solid var(--dev-border); border-radius: 6px; position: relative; &::before { content: ''; position: absolute; height: 24px; border-left: 1px dashed var(--dev-muted); top: -24px; left: 50%; } }.pool-heading { display: flex; align-items: center; gap: 8px; padding: 12px 12px 8px; strong { font-size: 13px; font-weight: 500; overflow-wrap: anywhere; flex: 1; } > span { font-size: 12px; color: var(--dev-muted); } > .icon { color: var(--dev-accent); } }.pool-summary { display: flex; flex-wrap: wrap; gap: 5px 12px; font-size: 12px; color: var(--dev-muted); padding: 0 12px 10px; }
.pool-capacity { margin: 0; padding: 0 12px 10px; color: var(--dev-muted); font-size: 12px; span { display: block; margin-top: 4px; } }
.pool-nodes { list-style: none; margin: 0; padding: 0; max-height: 140px; overflow-y: auto; li { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-top: 1px solid var(--dev-border); font-size: 13px; min-width: 0; > a, > span:nth-child(2) { min-width: 0; flex: 1; overflow-wrap: anywhere; } } }.readiness-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; background: var(--warning); &.ready { background: var(--success); } &.unavailable { background: var(--dev-muted); } }.node-state { color: var(--dev-muted); font-size: 12px; flex-shrink: 0; }
@media(max-width: 600px) { .membership-toolbar :deep(.scope-filter) { width: 100%; }.cluster-hub span { margin-left: 0; flex-basis: 100%; }.pool-grid { max-height: 320px; } }
</style>
